// Growth hooks: the ratings prompt, share links, first-party events and
// Apple Search Ads attribution. Factory-owned (src/core/), used by the app.
//
//   maybeAskForRating('first-route-saved')   at a HAPPY moment, never on launch
//   shareLink({ path, title, text })          a real link someone else can open
//   trackEvent('store_click', { store })      to the app's OWN server only
//   sendAdServicesToken()                     native iOS, behind a server flag
//
// PRIVACY BY DEFAULT. Events go to /api/growth/event on the app's own server
// (server/growth.mjs): no cookies, no device id, no third party. Apple Search
// Ads attribution is off until the server sets ADSERVICES_ATTRIBUTION=1, and
// `appfactory grow attribution --adservices on` declares it in App Privacy.
//
// NATIVE PARTS SHIP WITH THE NEXT NATIVE RELEASE. The in-app review sheet and
// the AdServices token are native APIs reached through Capacitor plugins
// ('InAppReview', 'AdServices'). Until a native build includes them, these
// functions feature-detect, do nothing, and return false — an over-the-air
// update cannot add a native plugin.
//
// Everything fails soft: a growth hook must never break the app.

import { prefs } from './storage.js';
import { isNative, platform, plugin, API_BASE, shareOrigin, shareUrl } from './native.js';

const RATING = { minSessions: 3, minDaysBetween: 120, maxPerYear: 3 };
const DAY = 86_400_000;

/** Count a session once per app start. Call from main.js (the chassis does). */
export function countSession() {
  try { prefs['growth.sessions'] = (prefs['growth.sessions'] ?? 0) + 1; } catch { /* storage unavailable */ }
}

/**
 * Ask for a rating — maybe. Rate-limited: not before `minSessions`, at most
 * once per `minDaysBetween`, at most `maxPerYear` (Apple caps the system
 * sheet at three a year anyway, silently). Returns true only if the native
 * sheet was requested; on the web it records nothing and returns false.
 */
export async function maybeAskForRating(moment, limits = {}) {
  try {
    const l = { ...RATING, ...limits };
    const now = Date.now();
    const sessions = prefs['growth.sessions'] ?? 0;
    const asked = (prefs['growth.ratingAsks'] ?? []).filter((t) => now - t < 365 * DAY);
    if (sessions < l.minSessions) return false;
    if (asked.length >= l.maxPerYear) return false;
    if (asked.length && now - Math.max(...asked) < l.minDaysBetween * DAY) return false;
    if (!isNative) return false;
    const review = plugin('InAppReview');
    if (!review?.requestReview) return false; // plugin ships with the next native release
    await review.requestReview();
    prefs['growth.ratingAsks'] = [...asked, now];
    trackEvent('rating_prompt', { moment: String(moment ?? '').slice(0, 40) });
    return true;
  } catch {
    return false;
  }
}

/** A shareable URL for an in-app path, tagged so the app's own server can count it. */
export function shareUrlFor(path = '/', campaign = 'share') {
  const base = shareOrigin().replace(/\/$/, '');
  const p = String(path).startsWith('/') ? path : `/${path}`;
  const q = new URLSearchParams({ utm_source: 'share', utm_medium: 'share', utm_campaign: campaign });
  return `${base}${p}${p.includes('?') ? '&' : '?'}${q}`;
}

/**
 * Share a link: the native share sheet, then the Web Share API, then the
 * clipboard. The page it opens carries its own Open Graph preview (written by
 * `npm run sync`), which is what shows up in the recipient's chat.
 * Returns 'shared' | 'copied' | 'aborted' | null.
 */
export async function shareLink({ path = '/', title = document.title, text = '', campaign = 'share' } = {}) {
  const url = shareUrlFor(path, campaign);
  try {
    const native = await shareUrl(url, title, text);
    if (native) { if (native === 'shared') trackEvent('share', { path: String(path).slice(0, 80) }); return native; }
    if (navigator.share) {
      await navigator.share({ title, text, url });
      trackEvent('share', { path: String(path).slice(0, 80) });
      return 'shared';
    }
    await navigator.clipboard?.writeText(url);
    return 'copied';
  } catch (e) {
    return e?.name === 'AbortError' ? 'aborted' : null;
  }
}

const EVENT_NAME = /^[a-z][a-z0-9_]{0,39}$/;

/**
 * A first-party event to the app's own server. Names are snake_case; props are
 * a few short strings or numbers — never anything that identifies a person.
 */
export function trackEvent(name, props = {}) {
  try {
    if (!EVENT_NAME.test(name)) return false;
    const clean = {};
    for (const [k, v] of Object.entries(props).slice(0, 8)) {
      if (typeof v === 'number' && Number.isFinite(v)) clean[k] = v;
      else if (typeof v === 'string') clean[k] = v.slice(0, 80);
    }
    const body = JSON.stringify({ name, props: clean, platform, path: location.pathname.slice(0, 120), ref: new URLSearchParams(location.search).get('utm_campaign') ?? undefined });
    const url = `${API_BASE}/api/growth/event`;
    if (!isNative && navigator.sendBeacon) return navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

/**
 * Apple Search Ads attribution: hand Apple's AdServices token to the app's own
 * server once, which exchanges it with Apple when ADSERVICES_ATTRIBUTION=1.
 * iOS native only; the plugin ships with the next native release.
 */
export async function sendAdServicesToken() {
  try {
    if (!isNative || platform !== 'ios' || prefs['growth.adServicesSent']) return false;
    const ads = plugin('AdServices');
    if (!ads?.attributionToken) return false;
    const { token } = await ads.attributionToken();
    if (!token) return false;
    const res = await fetch(`${API_BASE}/api/growth/adservices`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: token });
    if (res.ok || res.status === 404) prefs['growth.adServicesSent'] = true; // 404: the server has it switched off
    return res.ok;
  } catch {
    return false;
  }
}
